import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { EmptyState } from '@/components/ui/empty-state';
import { Time } from '@/components/ui/time';
import { Paginator } from '@/components/ui/paginator';
import { useStripeCheckout } from '@/hooks/useStripeCheckout';
import { useSubscriptions } from '@/hooks/useSubscriptions';
import { useAppCache } from '@/contexts/AppCacheContext';
import { getTierDisplayName } from '@/utils/tierUtils';
import { formatPriceCents } from '@/utils/formatPrice';

/**
 * Org-level Billing page.
 *
 * Lists every Stripe subscription owned by the current org's customer (active
 * and inactive), joined to its site row when `metadata.siteId` resolves.
 * "Manage Billing" deep-links to the Stripe customer portal — invoices,
 * payment methods, and cancellations all live there.
 */

const statusBadgeVariant = (
  status: string,
): 'success' | 'destructive' | 'warning' | 'default' => {
  switch (status) {
    case 'active':
    case 'trialing':
      return 'success';
    case 'past_due':
    case 'unpaid':
    case 'incomplete':
      return 'warning';
    case 'canceled':
    case 'incomplete_expired':
      return 'destructive';
    default:
      return 'default';
  }
};

const Billing: React.FC = () => {
  const { openBillingPortal, openSiteBillingPortal, loading: portalLoading } = useStripeCheckout();
  const { hasPermission } = useAppCache();
  const canManageBilling = hasPermission('manage:billing');
  const [page, setPage] = useState(1);
  const { data, isPending: loading, error } = useSubscriptions(page);
  // No client-side re-sort: the backend pages Stripe's subscription list,
  // which is already ordered newest-first by creation date.
  const subscriptions = data?.items ?? [];
  const pagination = data?.pagination;

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto pt-5 pb-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-foreground">Billing</h1>
            <p className="text-sm text-muted-foreground mt-2">
              Subscriptions, invoices, and payment methods for your organization.
            </p>
          </div>
          {canManageBilling && (
            <Button
              variant="primary"
              size="default"
              onClick={() => { void openBillingPortal(); }}
              disabled={portalLoading}
            >
              {portalLoading && <Spinner size="sm" className="mr-2" />}
              Manage billing
            </Button>
          )}
        </div>

        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4 stroke-[1.5]" />
            <AlertTitle>Couldn&rsquo;t load subscriptions</AlertTitle>
            <AlertDescription>{error.message}</AlertDescription>
          </Alert>
        )}

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[120px]">Status</TableHead>
              <TableHead>Site</TableHead>
              <TableHead>Template</TableHead>
              <TableHead className="w-[120px]">Type</TableHead>
              <TableHead className="w-[180px]">Price</TableHead>
              <TableHead className="w-[160px]">Created</TableHead>
              <TableHead className="w-[200px]">Renews / ends</TableHead>
              <TableHead className="w-[160px] text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <TableRow key={`skel-${i}`}>
                  <TableCell><Skeleton className="h-5 w-20" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-40" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-20" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-28" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-28" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                  <TableCell><Skeleton className="h-8 w-24 ml-auto" /></TableCell>
                </TableRow>
              ))
            ) : subscriptions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8}>
                  <EmptyState
                    title="No subscriptions yet"
                    description="When you publish a paid site, the subscription will appear here."
                    action={{ label: 'Go to sites', href: '/sites' }}
                  />
                </TableCell>
              </TableRow>
            ) : (
              subscriptions.map((sub) => {
                const isCanceled =
                  sub.status === 'canceled' || sub.status === 'incomplete_expired';
                return (
                  <TableRow key={sub.stripeSubscriptionId} className="group">
                    <TableCell>
                      <Badge variant={statusBadgeVariant(sub.status)}>
                        {sub.status.replace(/_/g, ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {sub.siteId ? (
                        <Link
                          to={`/sites/${sub.siteId}`}
                          className="font-medium text-foreground hover:underline"
                        >
                          {sub.siteName ?? sub.siteId}
                        </Link>
                      ) : (
                        <span className="text-muted-foreground italic">
                          (no linked site)
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-foreground">
                        {sub.templateName ?? '—'}
                      </span>
                    </TableCell>
                    <TableCell>
                      {sub.tier ? (
                        <span className="text-sm text-foreground capitalize">
                          {getTierDisplayName(sub.tier)}
                        </span>
                      ) : (
                        <span className="text-sm text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-foreground">
                        {isCanceled ? '—' : formatPriceCents(sub.monthlyPrice)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        <Time value={sub.createdAt} format="medium" />
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {isCanceled ? 'Ended ' : sub.cancelAtPeriodEnd ? 'Cancels ' : 'Renews '}
                        <Time value={sub.currentPeriodEnd} format="medium" />
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      {/* Hidden until the row is hovered or any descendant
                          gains focus (keyboard users tabbing through still
                          see the action). Reserves space via `invisible`
                          rather than `hidden` so the column stays aligned. */}
                      {canManageBilling && (
                        <Button
                          variant="tertiary"
                          size="dense"
                          onClick={() => {
                            // Deep-link to the row's specific subscription when
                            // we know the site. Subscriptions with no linked
                            // site fall back to the org-wide portal.
                            if (sub.siteId) {
                              void openSiteBillingPortal(sub.siteId);
                            } else {
                              void openBillingPortal();
                            }
                          }}
                          disabled={portalLoading}
                          className="invisible group-hover:visible group-focus-within:visible"
                        >
                          {portalLoading && <Spinner size="sm" className="mr-2" />}
                          Manage in Stripe
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>

        <Paginator
          pagination={pagination}
          onPageChange={setPage}
          className="mt-6"
        />

        <p className="mt-6 text-xs text-muted-foreground">
          Invoices and payment methods are managed in the Stripe customer portal —
          click &ldquo;Manage billing&rdquo; above.
        </p>
      </div>
    </div>
  );
};

export default Billing;
